import React from 'react';
import { DynamicFormModal, StapleTableCell, StapleTableRow } from '@insurancemenu/imenu-components-lib/dist/components';
import { TableActionsDropdown } from 'components/common/general/TableActionsDropdown';
import SubscriberDependentDelegate from 'store/modules/elections/dependents/subDependentDelegate';
import { Delete, Edit } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useUpdateDependentModalStore } from 'store/modules/elections/dependents/updateDependentModalStore';
import { useRemoveDependentModalStore } from 'store/modules/elections/dependents/removeDependentModalStore';
import { observer } from 'mobx-react-lite';
import ActionModal from '@insurancemenu/imenu-components-lib/dist/components/modals/ActionModal';
import { SUBSCRIBER_TYPES } from 'types/electionService';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import SubscriberDelegate from 'store/modules/elections/subscriberDelegate';
import { MemberTypeTagStyled, MoreDropdownCellStyled } from './styles';
import {
  DEPENDENT_TABLE_ROW_TEST_ID,
  DEPENDENT_FULL_NAME_TEST_ID,
  DEPENDENT_MEMBER_TYPE_TEST_ID,
  DEPENDENT_DATE_OF_BIRTH_TEST_ID,
  DEPENDENT_ACTIONS_DROPDOWN_TEST_ID,
  UPDATE_DEPENDENT_MODAL_TEST_ID,
  REMOVE_DEPENDENT_MODAL_TEST_ID,
} from '../constants';

interface Props {
  dependentDelegate: SubscriberDependentDelegate;
  subscriberDelegate: SubscriberDelegate;
  formId: string;
}

interface DependentActionsDropdownProps {
  onUpdateDependent: () => void;
  onRemoveDependent: () => void;
}

const DependentActionsDropdown: React.FC<DependentActionsDropdownProps> = ({
  onUpdateDependent,
  onRemoveDependent,
}) => {
  return (
    <TableActionsDropdown
      name="dependent-actions"
      data-test-id={DEPENDENT_ACTIONS_DROPDOWN_TEST_ID}
      options={[
        {
          label: 'Edit Dependent',
          value: 'editDependent',
          onOptionClick: onUpdateDependent,
          icon: Edit,
        },
        {
          label: 'Remove Dependent',
          value: 'removeDependent',
          customColor: 'red',
          onOptionClick: onRemoveDependent,
          icon: Delete,
        },
      ]}
    />
  );
};

const DependentTableRow: React.FC<Props> = observer(({ dependentDelegate, subscriberDelegate, formId }) => {
  const {
    mainData: { fullName, dateOfBirth, memberType },
  } = dependentDelegate;

  const updateDependentModalStore = useUpdateDependentModalStore({
    dependentDelegate,
    subscriberDelegate,
    formId,
  });

  const removeDependentModalStore = useRemoveDependentModalStore({
    dependentDelegate,
    subscriberDelegate,
  });

  return (
    <StapleTableRow data-test-id={DEPENDENT_TABLE_ROW_TEST_ID}>
      <StapleTableCell width="10rem" data-test-id={DEPENDENT_FULL_NAME_TEST_ID}>
        {fullName}
      </StapleTableCell>
      <StapleTableCell width="10rem" data-test-id={DEPENDENT_MEMBER_TYPE_TEST_ID}>
        <MemberTypeTagStyled $memberType={memberType}>{SUBSCRIBER_TYPES[memberType]}</MemberTypeTagStyled>
      </StapleTableCell>
      <StapleTableCell width="10rem" data-test-id={DEPENDENT_DATE_OF_BIRTH_TEST_ID}>
        {dateOfBirth}
      </StapleTableCell>
      <MoreDropdownCellStyled>
        {updateDependentModalStore && removeDependentModalStore && (
          <>
            <DependentActionsDropdown
              onUpdateDependent={updateDependentModalStore.openUpdateDependentModal}
              onRemoveDependent={removeDependentModalStore.openRemoveDependentModal}
            />
            {updateDependentModalStore.showUpdateDependentModal && (
              <DynamicFormModal
                show={updateDependentModalStore.showUpdateDependentModal}
                onClose={updateDependentModalStore.closeUpdateDependentModal}
                formDelegate={updateDependentModalStore.dynamicJSONFormDelegate}
                modalMaxWidth="90rem"
                data-test-id={UPDATE_DEPENDENT_MODAL_TEST_ID}
                completeButtonText="Save"
              />
            )}
            <ActionModal
              show={removeDependentModalStore.showRemoveDependentModal}
              title="Remove Dependent"
              subtitle="Are you sure you want to remove this dependent?"
              actionButtonText="Remove Dependent"
              closeButtonText="Cancel"
              onClose={removeDependentModalStore.closeRemoveDependentModal}
              onAction={removeDependentModalStore.removeDependent}
              loading={removeDependentModalStore.isLoading}
              modalType={ModalType.DELETE}
              data-test-id={REMOVE_DEPENDENT_MODAL_TEST_ID}
            />
          </>
        )}
      </MoreDropdownCellStyled>
    </StapleTableRow>
  );
});

export default DependentTableRow;
